import { listChatActivitiesConnectionKey } from 'embeds/deprecated/fragments';
import { forEach, get, has, isEmpty, isNumber } from 'lodash';
import { put, select, takeEvery } from 'redux-saga/effects';
import ConnectionHandler from 'relay-connection-handler-plus';
import { commitLocalUpdate } from 'relay-runtime';
import { createAsyncAction, getType } from 'typesafe-actions';
import { LoggerStatusEnum, sendLog } from 'ui-model';

import { selectCurrentUser } from '@lib/selectors';
import environment from '@lib/utils/RelayEnvironment';

import { updateFetchKeyDelayed } from 'actions';
import { ON_SOCKET_CHAT_ACTIVITY } from 'containers/App/constants';
import { invalidateConnections, toGlobalId } from 'services/relayService';

export const actionChatActivityReassign = createAsyncAction(
  'actionChatActivityReassign/REQUEST',
  'actionChatActivityReassign/SUCCESS',
  'actionChatActivityReassign/FAILURE',
)<any, any, any>();

export const actionChatActivityOpen = createAsyncAction(
  'actionChatActivityOpen/REQUEST',
  'actionChatActivityOpen/SUCCESS',
  'actionChatActivityOpen/FAILURE',
)<any, any, any>();

export const actionChatActivityClose = createAsyncAction(
  'actionChatActivityClose/REQUEST',
  'actionChatActivityClose/SUCCESS',
  'actionChatActivityClose/FAILURE',
)<any, any, any>();

export const actionChatActivityClear = createAsyncAction(
  'actionChatActivityClear/REQUEST',
  'actionChatActivityClear/SUCCESS',
  'actionChatActivityClear/FAILURE',
)<any, any, any>();

export const actionChatActivityUnread = createAsyncAction(
  'actionChatActivityUnread/REQUEST',
  'actionChatActivityUnread/SUCCESS',
  'actionChatActivityUnread/FAILURE',
)<any, any, any>();

const typeName = 'ChatActivity';

function* sagaWatcher(action: any) {
  try {
    const chatActivity =
      get(action, 'payload.data') ||
      get(action, 'data') ||
      (action.chatActivityId && { _id: action.chatActivityId }) ||
      action;

    // Each action needs to include, at least, the ChatActivity._id
    const { _id } = chatActivity;
    if (!_id) {
      sendLog('sagaWatchersChatActivity', { error: '_id missing', action }, LoggerStatusEnum.error);
      return;
    }

    // @ts-ignore
    const currentUser = yield select(selectCurrentUser);

    commitLocalUpdate(environment, (store) => {
      try {
        // Update viewer counts using socket data
        const currentUserGlobalId = currentUser && toGlobalId('User', currentUser.id);
        const viewerRecord = currentUserGlobalId && store.get(currentUserGlobalId);
        if (viewerRecord) {
          if (isNumber(chatActivity.unreadCount)) {
            viewerRecord.setValue(chatActivity.unreadCount, 'unreadChatsCount');
          }
          if (isNumber(chatActivity.unassignedCount)) {
            viewerRecord.setValue(chatActivity.unassignedCount, 'chatActivityUnassignedCount');
          }
        }

        const chatActivityRecord = store.get(toGlobalId('ChatActivity', _id));
        if (chatActivityRecord) {
          // TODO optimistic updates for ChatActivity
          if (has(chatActivity, 'status')) {
            chatActivityRecord.setValue(chatActivity.status, 'status');
          }
          if (has(chatActivity, 'deleted')) {
            chatActivityRecord.setValue(chatActivity.deleted, 'deleted');
          }
          chatActivityRecord.invalidateRecord();
        }

        // Invalidate stored connections
        // There might be different versions of the same connection with different filters
        // so we invalidate them in order to refetch the next time we select a different tab
        invalidateConnections(ConnectionHandler.getConnections(store.getRoot(), listChatActivitiesConnectionKey));
      } catch (error) {
        sendLog('sagaWatchersChatActivity', { error, action }, LoggerStatusEnum.error);
      }
    });
    yield put(updateFetchKeyDelayed(typeName));
  } catch (error) {
    sendLog('sagaWatchersChatActivity', { error, action }, LoggerStatusEnum.error);
  }
}

// eslint-disable-next-line require-yield
function* sagaWatcherChatActivityClear(action: any) {
  try {
    const _id = get(action, 'payload._id');
    if (!_id) {
      sendLog('sagaWatcherChatActivityClear _id not found', { action }, LoggerStatusEnum.warn);
      return;
    }

    commitLocalUpdate(environment, (store) => {
      const chatActivityRecord = store.get(toGlobalId('ChatActivity', _id));
      if (!chatActivityRecord) {
        sendLog('sagaWatcherChatActivityClear record not found', { action }, LoggerStatusEnum.warn);
        return;
      }
      // Optimistic update for `readByUser`
      chatActivityRecord.setValue(true, 'readByUser');
    });
  } catch (error) {
    sendLog('sagaWatcherChatActivityClear', { error, action }, LoggerStatusEnum.error);
  }
}

// eslint-disable-next-line require-yield
function* sagaWatcherChatActivityUnread(action: ReturnType<typeof actionChatActivityUnread.success>) {
  try {
    const ids = action.payload.requestPayload;
    if (isEmpty(ids)) {
      sendLog('sagaWatcherChatActivityUnread ids not found', { action }, LoggerStatusEnum.warn);
      return;
    }

    commitLocalUpdate(environment, (store) => {
      forEach(ids, (_id) => {
        const chatActivityRecord = store.get(toGlobalId('ChatActivity', _id));
        if (!chatActivityRecord) {
          return;
        }
        // Dynamic update for `readByUser`
        chatActivityRecord.setValue(false, 'readByUser');
      });
    });
  } catch (error) {
    sendLog('sagaWatcherChatActivityUnread', { error, action }, LoggerStatusEnum.error);
  }
}

export default function* sagaWatchersChatActivity() {
  yield takeEvery(getType(actionChatActivityReassign.success), sagaWatcher);
  yield takeEvery(getType(actionChatActivityOpen.success), sagaWatcher);
  yield takeEvery(getType(actionChatActivityClose.success), sagaWatcher);
  yield takeEvery(getType(actionChatActivityClear.request), sagaWatcherChatActivityClear);
  yield takeEvery(getType(actionChatActivityUnread.success), sagaWatcherChatActivityUnread);
  yield takeEvery(ON_SOCKET_CHAT_ACTIVITY, sagaWatcher);
}
